import React, { createContext, useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import { doc, onSnapshot } from "firebase/firestore";
import { ensureUserProfile } from "../services/userService";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileLoading, setProfileLoading] = useState(false);
    const [userProfile, setUserProfile] = useState(null);

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') console.log("🔐 AuthContext: Starting auth state listener...");
        
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (process.env.NODE_ENV !== 'production') console.log("🔐 AuthContext: Auth state changed:", user ? `User ${user.email}` : "No user");
            setCurrentUser(user);
            setLoading(false); // Arrêter le chargement une fois que l'utilisateur est vérifié
        });

        return () => {
            if (process.env.NODE_ENV !== 'production') console.log("🔐 AuthContext: Cleaning up auth listener");
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        let unsubscribeProfile = null;

        const watchProfile = async () => {
            if (!currentUser) {
                setUserProfile(null);
                setProfileLoading(false);
                return;
            }

            setProfileLoading(true);

            try {
                await ensureUserProfile(currentUser);
            } catch (error) {
                console.error("Erreur lors de la préparation du profil utilisateur :", error);
            }

            const userDocRef = doc(db, "users", currentUser.uid);
            unsubscribeProfile = onSnapshot(
                userDocRef,
                (snapshot) => {
                    if (snapshot.exists()) {
                        setUserProfile({ id: snapshot.id, ...snapshot.data() });
                    } else {
                        setUserProfile(null);
                    }
                    setProfileLoading(false);
                },
                (error) => {
                    console.error("Erreur lors de l'écoute du profil utilisateur :", error);
                    setUserProfile(null);
                    setProfileLoading(false);
                }
            );
        };

        watchProfile();

        return () => {
            if (unsubscribeProfile) {
                unsubscribeProfile();
            }
        };
    }, [currentUser]);

    return (
        <AuthContext.Provider value={{ currentUser, loading, profileLoading, userProfile }}>
            {loading ? (
                // Afficher un loader pendant que Firebase vérifie l'état d'authentification
                <div className="min-h-screen flex items-center justify-center bg-white">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-gray-600">Chargement...</p>
                    </div>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};
